import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Modal } from '../Modal';
import { SentenceWithPhone } from '../SentenceWithPhone';
import { CodeStepBody } from './onboarding/CodeScreen';
import { PhoneStepBody } from './onboarding/PhoneScreen';
import { Typography } from './ui/Typography';
import { useCodeVerification, type CodeVerificationInput } from '../../lib/use-code-verification';
import { clearPendingVerification, readPendingVerification, savePendingVerification } from '../../lib/pending-verification';
import { useGenericBi } from '../../lib/use-generic-bi';
import { PHONE_VERIFY_PAGE } from '../../lib/use-phone-verification';
import { useTranslation } from '../../lib/i18n';
import styles from './PhoneVerifyModal.module.css';

/** The `?verify=` values that open a step. Anything else leaves the modal shut. */
export const VERIFY_PHONE = 'phone';
export const VERIFY_CODE = 'code';
/**
 * The number step, opened by the caller from the rail instead of by the assistant. Same step and same
 * flow — a separate value only because the two arrive with different wording: the assistant is already
 * mid-sentence about why it needs a number, where the rail has to make the case from nothing.
 */
export const VERIFY_CONNECT = 'connect';

/**
 * Phone verification over the conversation.
 *
 * Which step is open lives in `?verify=phone|code`, so the destination the assistant navigates to is
 * a URL like any other. What was SENT — the number, the verificationId — deliberately does not: it is
 * held here in memory and handed to the code step directly. The standalone `/onboarding/code` page
 * takes those in its query string, and an instrumented page publishes its own location, so that page
 * hands the number and the verificationId to every analytics tag on it (measured: Facebook, Google
 * Ads, DoubleClick, two Google endpoints, the GTM container and frog all received them).
 *
 * A reload still lands on the code step, because the sent code is kept for the tab in
 * `sessionStorage` (see `pending-verification`) — storage is not something an analytics tag reads, where
 * the location is something every one of them sends. When it genuinely is not there, the step falls back
 * to sending again rather than showing a code field with no number behind it.
 *
 * Closing strips the verify param and leaves the rest of the URL alone, so the conversation the user
 * was in stays open behind it.
 */
export function PhoneVerifyModal() {
  const router = useRouter();
  const { t } = useTranslation();
  const [sent, setSent] = useState<CodeVerificationInput['sent']>(undefined);
  const step = router.query.verify;
  // The step is in the URL, so the SERVER knows it too — but `Modal` portals to `document.body` and
  // renders nothing without a document. That asymmetry is a hydration mismatch: markup on the client,
  // none from the server. Waiting for the mount makes both passes agree on "nothing", and the modal
  // appears on the commit after.
  const [mounted, setMounted] = useState(false);
  // Same pass picks up a code already sent from this tab, which is what makes a reload land back on the
  // code step. It has to happen here rather than in the initial state for the same reason as the gate:
  // there is no `sessionStorage` on the server.
  useEffect(() => {
    setSent(readPendingVerification());
    setMounted(true);
  }, []);
  const open = mounted && (step === VERIFY_PHONE || step === VERIFY_CONNECT || step === VERIFY_CODE);
  // Which ask opened the dialog, remembered rather than re-read: the code step replaces `?verify=` with
  // its own value, and "use a different number" has to return to the ask the user came in on. A reload
  // straight onto the code step has no entrance to recover, and falls back to the assistant's wording.
  //
  // Each ENTRANCE sets it, and closing deliberately does not clear it. Clearing on the way out would
  // re-render the dialog in the other ask's words for the frame between that and the URL losing
  // `?verify=`, which reads as the wording changing just as the user dismisses it.
  const [fromRail, setFromRail] = useState(false);
  useEffect(() => {
    if (step === VERIFY_CONNECT) setFromRail(true);
    else if (step === VERIFY_PHONE) setFromRail(false);
  }, [step]);

  /**
   * The dialog reports itself opening, once per opening rather than per step: moving from the phone step to
   * the code step is progress through one dialog, not a second one.
   *
   * It is the only event that says the ask was actually PUT to someone. The assistant navigating here, the
   * user reopening it from a card, and a reload landing back on the code step all arrive the same way — as a
   * `?verify=` URL — so without this the funnel starts at the first keystroke in the number field and every
   * user who saw the dialog and closed it is indistinguishable from one who was never asked.
   */
  const { logModalActionBi } = useGenericBi('verify-phone');
  useEffect(() => {
    if (!open) return;
    logModalActionBi({ ...PHONE_VERIFY_PAGE, modal_name: 'verify_phone', action: 'open' });
    // Keyed on `open` alone: a step change inside the dialog must not re-report it as a new opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toStep = (next: string | undefined) => {
    const { verify, ...rest } = router.query;
    const query = next ? { ...rest, verify: next } : rest;
    void router.replace({ pathname: router.pathname, query }, undefined, { shallow: true });
  };

  // Every exit from the flow drops the stored code, so a number never outlives the step that sent it.
  const forget = () => {
    clearPendingVerification();
    setSent(undefined);
  };

  const close = () => {
    forget();
    toStep(undefined);
  };

  if (!open) {
    return null;
  }

  // `verify=code` with nothing sent means the code is genuinely gone — a different tab, or a store that
  // would not keep it — so it shows the phone step: re-sending is the honest recovery, where a code
  // field with no number behind it would verify against nothing.
  return step === VERIFY_CODE && sent ? (
    <CodeStepModal
      sent={sent}
      onClose={close}
      onUseAnotherNumber={() => {
        forget();
        toStep(fromRail ? VERIFY_CONNECT : VERIFY_PHONE);
      }}
    />
  ) : (
    // Deliberately NOT on the 650 cap + `scrollBody` every other modal takes (Monday 12771405229): the
    // number step's country picker is an absolutely-positioned dropdown, and a scroll container anywhere
    // above it traps the list in a scroll box instead of letting it overlay (the same reason the shell's
    // full-screen variant adds no overflow). The two steps are a field and a button — neither comes near
    // 650 — so capping without a scroll would only risk clipping the picker.
    <Modal
      open
      onClose={close}
      size="minimum"
      fullScreenOnSmall

      labelledBy="verify-phone-title"
    >
      <Typography variant="h1" align="center" id="verify-phone-title" className={styles.title}>
        {t('onboarding.phone.connect.title')}
      </Typography>
      <Typography variant="p2" align="center" className={styles.subtitle}>
        {t('onboarding.phone.connect.subtitle')}
      </Typography>
      <PhoneStepBody
        onCodeSent={(next) => {
          savePendingVerification(next);
          setSent(next);
          toStep(VERIFY_CODE);
        }}
      />
    </Modal>
  );
}

/**
 * Its own component so the code step's hook mounts only while that step is open — the hook
 * auto-submits off the params it finds, so mounting it behind a closed modal would fire the verify
 * before the user ever saw the field.
 */
function CodeStepModal({
  sent,
  onClose,
  onUseAnotherNumber,
}: {
  sent: CodeVerificationInput['sent'];
  onClose: () => void;
  onUseAnotherNumber: () => void;
}) {
  const { t } = useTranslation();
  // Verified from inside a conversation, so the modal just closes: the number is on file and the
  // thread the user was in is still behind it, where the page's own success goes on to the home entry.
  const code = useCodeVerification({ sent, onVerified: onClose, onUseAnotherNumber });
  const phone = code.phone || t('onboarding.code.yourNumber');

  return (
    // No 650 cap here either — the step is one code field, and "use a different number" returns to the
    // phone step in the same shell (see the note there).
    <Modal
      open
      onClose={onClose}
      size="minimum"
      fullScreenOnSmall

      labelledBy="verify-code-title"
    >
      <Typography variant="h1" align="center" id="verify-code-title" className={styles.title}>
        {t('onboarding.code.symphony.title')}
      </Typography>
      <Typography variant="p2" align="center" className={styles.subtitle}>
        <SentenceWithPhone line={t('onboarding.code.symphony.subtitle', { phone })} phone={phone} />
      </Typography>
      <CodeStepBody code={code} />
    </Modal>
  );
}
